// 🔑 THE GUIDED REVIEW TAB, STRIPPED BACK (Jesse, 2026-10-24: "too cluttered and duplicates the Underwriting Module").
// It shows only: (1) the step header as it is (GuidedStepHeader); (2) the step strip with dates; (3) the open items on
// the current step — Upload / Dismiss, joint applicants side by side; (4) once step 8 is reached, a compact decision card
// (outcome, maximum, notices sent and to whom, any open review task); (5) "Open in Underwriting Module". The fraud status
// is one line. Every other action — the step panels, letters, pricing, re-price, override, uphold / overturn, consents —
// is in the Underwriting Module (components/admin/guidedReview/GuidedModuleActions + ReviewModule's guided steps).
// Approved mockups: claude.ai/artifact/TUEkZtMg8rT2sB6spSem8z. Brand colors only (parts.jsx B).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import GuidedStepHeader, { staffDetailLine } from '../GuidedStepHeader';
import { GUIDED_STEPS, stepByNumber } from '../../../constants/guidedFlowSteps';
import { fraudStaffStatus } from '../../../utils/fraudStaffStatus';
import { API, B, day, stamp, money, nameOf, currentStepOf, itemsForStep, reasonWords, ReasonAction, btn, Label } from './parts';

export { itemsForStep };

// The Module step a guided step's work lives on (ReviewModule STEPS + its guided steps).
export const MODULE_STEP_FOR = { 1: 'identity', 2: 'identity', 3: 'identity', 4: 'identity', 5: 'income', 6: 'income', 7: 'credit', 8: 'decision' };

const OUTCOME_WORDS = { approved: 'Approved', counteroffer_sent: 'Counteroffer', counteroffer: 'Counteroffer', adverse: 'Declined',
  declined: 'Declined', review: 'Held for review', review_pending: 'Held for review' };
// 🔑 approved_terms_counteroffer_* (2026-10-29): approved at the price, with a minimum down payment above the planned one.
const outcomeWord = (o) => OUTCOME_WORDS[String(o || '')] || (/^review/.test(String(o || '')) ? 'Held for review'
  : /^approved_terms_counteroffer/.test(String(o || '')) ? 'Counteroffer (down payment)'
  : /^counteroffer/.test(String(o || '')) ? 'Counteroffer' : String(o || '—'));

function FraudLine({ user, fraudFlags, idVerification }) {
  if (!fraudFlags && !user) return null;
  const s = fraudStaffStatus(user, fraudFlags, idVerification);
  const dot = s.tone === 'red' ? B.red : s.tone === 'amber' ? B.orange : B.green;
  return (
    <div data-testid="guided-review-fraud-line" data-tone={s.tone} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: B.ink, minWidth: 0 }}>
      <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: '50%', background: dot, boxShadow: `0 0 0 1px ${B.ink}`, flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}{s.clear ? '' : ' · act on it in the Underwriting Module'}</span>
    </div>
  );
}

function StepStrip({ view }) {
  return (
    <div data-testid="guided-review-strip" style={{ display: 'grid', gap: 8 }}>
      {view.applicants.map((a) => {
        const cur = currentStepOf(a);
        const gateSteps = (a.state && a.state.gate && a.state.gate.steps) || [];
        return (
          <div key={a.userId} style={{ display: 'grid', gap: 4 }}>
            {view.applicants.length > 1 && <div style={{ fontSize: 12, color: B.navy, fontWeight: 700 }}>{nameOf(a)}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, minmax(0, 1fr))', gap: 6 }}>
              {GUIDED_STEPS.map((s) => {
                const g = gateSteps.find((x) => Number(x.n) === s.n);
                const done = g ? g.complete && s.n < cur : s.n < cur;
                const here = s.n === cur;
                const f = a.facts && a.facts.steps && a.facts.steps[s.n];
                return (
                  <div key={s.n} data-testid="guided-review-strip-step" data-step={s.n} data-current={here ? 'true' : 'false'} title={`${s.n} ${s.label}${f && f.how ? ` · ${f.how}` : ''}`}
                    style={{ borderRadius: 8, padding: here ? '5px 7px' : '6px 8px', minWidth: 0, fontSize: 12, lineHeight: 1.35,
                      background: here ? B.tan : done ? B.cyanLight : B.white, border: here ? `2px solid ${B.gold}` : `1px solid ${done ? B.cyanLight : B.line}` }}>
                    <div style={{ fontWeight: 700, color: B.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.n} {s.label}</div>
                    <div style={{ fontSize: 11, color: done || here ? B.navy : B.slate }}>{done ? (day(f && f.at) || 'done') : here ? `current${f && f.reachedAt ? ` · since ${day(f.reachedAt)}` : ''}` : '—'}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 🔑 STEP 8'S BLOCKERS, FROM THE ONE SOURCE (hotfix 11-01 → push B C3, Jesse 2026-10-31 — backend utils/fileBlockers,
 * served in GET /guided-review as view.blocking): EVERY blocker, in the order the status card ranks them (staff →
 * borrower → third party), so step 8 never says "Nothing open" while the file is blocked. Each applicant's card lists its
 * own; anything not tied to an applicant shows on the primary's. Never re-ranked or re-worded here.
 */
export function blockingItemsFor(blocking, a, applicantIds = []) {
  if (!blocking || !a) return [];
  const mine = (uid) => (uid != null && applicantIds.includes(Number(uid)) ? Number(uid) === Number(a.userId) : a.role === 'primary');
  const isMine = (i) => {
    const on = (i.userIds && i.userIds.length ? i.userIds : i.userId != null ? [i.userId] : []).filter((u) => applicantIds.includes(Number(u)));
    return on.length ? on.some((u) => mine(u)) : a.role === 'primary';
  };
  return (blocking.items || []).filter(isMine).map((i) => ({ key: `blocking:${i.code || i.text}`, text: i.line || i.text,
    // A borrower's request keeps its own Upload / Dismiss (the need's controls below).
    ...(i.needId ? { staff: { kind: 'need', needId: i.needId, category: i.category || null, source: i.source || null } } : {}),
    blocking: { owner: i.owner, module: i.link === 'module' ? i.module : null, moduleLabel: i.moduleLabel, action: i.action } }));
}

/** The open items on the applicant's current step, with Upload / Dismiss on each need. */
function OpenItems({ a, api, onChanged, onOpenModule, blocking, applicantIds }) {
  const n = currentStepOf(a);
  const items = [...itemsForStep(a, n), ...(n === 8 ? blockingItemsFor(blocking, a, applicantIds) : [])];
  const fileRef = useRef({});
  const upload = async (needId, files) => {
    if (!files || !files.length) return;
    const fd = new FormData();
    fd.append('need_id', String(needId));
    for (const f of files) fd.append('files', f);
    await api.post(`/borrowers/${a.userId}/upload-need-document`, fd);
    await onChanged();
  };
  return (
    <div data-testid="guided-review-applicant" data-step={n} style={{ background: B.white, border: `1px solid ${B.line}`, borderRadius: 12, padding: '14px 16px', display: 'grid', gap: 10, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: B.ink }}>{nameOf(a)}</span>
        <span style={{ fontSize: 12, color: B.slate }}>{a.role === 'primary' ? 'primary' : 'co-borrower'} · step {n} · {items.length} open</span>
      </div>
      {!items.length && <div data-testid="guided-review-none-needed" style={{ fontSize: 13, color: B.slate }}>Nothing open on this step.</div>}
      {items.map((it) => {
        const needId = it.staff && it.staff.kind === 'need' ? it.staff.needId : null;
        return (
          <div key={it.key} data-testid="guided-review-item" data-key={it.key} data-owner={it.blocking ? it.blocking.owner : undefined} style={{ borderTop: `1px solid ${B.line}`, paddingTop: 10, display: 'grid', gap: 6 }}>
            <div data-testid="guided-review-item-text" style={{ fontSize: 13, fontWeight: 600, color: B.ink, whiteSpace: 'pre-line' }}>{it.text}</div>
            <div style={{ fontSize: 12, color: B.slate }}>{staffDetailLine(it.staff)}</div>
            {it.blocking && it.blocking.module && it.blocking.action && (
              <button type="button" data-testid="guided-review-item-blocking-action" data-module={it.blocking.module} data-owner={it.blocking.owner}
                onClick={() => onOpenModule(it.blocking.module === 'misc' ? 'documents' : it.blocking.module)} style={{ ...btn('primary'), justifySelf: 'start' }}>
                {it.blocking.moduleLabel || it.blocking.module} → {it.blocking.action}
              </button>
            )}
            {/* A whole step-5 section (Income, Assets, Housing) is not one request, so it has no Upload / Dismiss of its
                own: staff work it in the Module's Documents and needs step (Jesse, 2026-10-24). */}
            {/* 🔑 Steps 6–7: the Module step that does each staff move (backend utils/stepBlockers, Jesse 2026-10-26). */}
            {it.staff && it.staff.kind === 'blockers' && it.staff.actions.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {it.staff.actions.map((x) => (
                  <button key={`${x.module}:${x.action}`} type="button" data-testid="guided-review-item-step-action" data-module={x.module}
                    onClick={() => onOpenModule(x.module === 'misc' ? 'documents' : x.module)} style={btn('primary')}>{x.action}</button>
                ))}
              </div>
            )}
            {!needId && it.staff && it.staff.kind === 'documents' && (
              // 🔑 The section's own Module step (Jesse, 2026-10-26): Docs is read-only now; a section's items are on its step.
              <button type="button" data-testid="guided-review-item-open-docs"
                data-module={/assets/i.test(it.borrowerText || it.text) ? 'assets' : /housing/i.test(it.borrowerText || it.text) ? 'housing' : 'income'}
                onClick={() => onOpenModule(/assets/i.test(it.borrowerText || it.text) ? 'assets' : /housing/i.test(it.borrowerText || it.text) ? 'housing' : 'income')}
                style={{ justifySelf: 'start', background: 'none', border: 'none', padding: 0, minHeight: 32, color: B.blue, fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
                Open in the Underwriting Module
              </button>
            )}
            {needId && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <input type="file" multiple aria-label={`Upload for ${it.text}`} style={{ display: 'none' }} ref={(el) => { fileRef.current[needId] = el; }}
                  onChange={(e) => upload(needId, e.target.files)} />
                <button type="button" data-testid="guided-review-item-upload" onClick={() => fileRef.current[needId] && fileRef.current[needId].click()} style={btn('primary')}>Upload</button>
                <ReasonAction label="Dismiss" confirmLabel="Dismiss item" testid="guided-review-item-dismiss"
                  onConfirm={async (reason) => { await api.patch(`/borrowers/${a.userId}/additional-needs/${needId}`, { status: 'dismissed', reason }); await onChanged(); }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Step 8: one compact card — outcome, maximum, notices and to whom, and the review task. */
function DecisionCard({ view, onOpenModule }) {
  const s8 = view.step8 || {};
  const dec = s8.decision;
  const rv = s8.review;
  const openTask = rv && rv.status === 'open';
  return (
    <div data-testid="guided-review-decision-card" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, alignItems: 'start' }}>
      <div data-testid="guided-review-decision" style={{ background: B.white, border: `1px solid ${B.line}`, borderRadius: 12, padding: '16px 18px', display: 'grid', gap: 12, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: B.ink }}>Decision</span>
          {dec && dec.decidedAt && <span style={{ fontSize: 12, color: B.slate }}>decided {stamp(dec.decidedAt)}</span>}
        </div>
        {!dec ? <div style={{ fontSize: 13, color: B.slate }}>No decision yet.</div> : (<>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
            <div style={{ background: B.ground, borderRadius: 8, padding: '10px 12px' }}>
              <Label>Outcome</Label>
              <div data-testid="guided-review-decision-outcome" style={{ fontSize: 16, fontWeight: 700, color: B.ink, marginTop: 4 }}>{openTask ? 'Held for review' : outcomeWord(dec.outcome)}</div>
            </div>
            <div style={{ background: B.ground, borderRadius: 8, padding: '10px 12px' }}>
              <Label>Maximum</Label>
              <div data-testid="guided-review-decision-max" style={{ fontSize: 16, fontWeight: 700, color: B.ink, marginTop: 4 }}>{money(dec.approvedMax)}</div>
              {dec.requestedPurchasePrice ? <div style={{ fontSize: 12, color: B.navy }}>asked {money(dec.requestedPurchasePrice)} at step 2</div> : null}
            </div>
          </div>
          <div style={{ display: 'grid', gap: 4 }}>
            <Label>Notices sent</Label>
            {(s8.notices || []).length === 0 && <div style={{ fontSize: 13, color: B.slate }}>None.</div>}
            {(s8.notices || []).map((x) => {
              const who = view.applicants.find((a) => a.userId === x.userId);
              return (
                <div key={x.id} data-testid="guided-review-notice" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 13, borderTop: `1px solid ${B.line}`, paddingTop: 6 }}>
                  <span>{x.kind === 'counteroffer' ? 'Counteroffer notice' : 'Adverse action notice'} → {who ? nameOf(who) : `#${x.userId}`}{x.toEmail ? ` (${x.toEmail})` : ''}</span>
                  <span style={{ color: B.slate }}>{stamp(x.sentAt)}{x.delivery ? ` · ${x.delivery}` : ''}</span>
                </div>
              );
            })}
          </div>
        </>)}
      </div>
      <div data-testid="guided-review-task" data-status={rv ? rv.status : 'none'} style={{ background: openTask ? B.orangeLight : B.white, border: `1px solid ${openTask ? B.orange : B.line}`, borderRadius: 12, padding: '16px 18px', display: 'grid', gap: 8, minWidth: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: B.ink }}>Review task</span>
        {!rv && <div style={{ fontSize: 13, color: B.ink }}>None.</div>}
        {rv && openTask && (<>
          <div style={{ fontSize: 13, fontWeight: 700, color: B.ink }}>Open · task #{rv.id}{rv.dueAt ? ` · due ${day(rv.dueAt)}` : ''}</div>
          <div data-testid="guided-review-task-reason" style={{ fontSize: 13, color: B.ink }}>{reasonWords(rv.routeReason)}</div>
          <button type="button" data-testid="guided-review-task-open-module" onClick={() => onOpenModule('decision')} style={{ ...btn('primary'), justifySelf: 'start' }}>Uphold or overturn in the Underwriting Module</button>
        </>)}
        {rv && !openTask && (<>
          <div style={{ fontSize: 13, color: B.ink }}>None open.</div>
          <div style={{ background: B.ground, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: B.ink }}>
            Last: #{rv.id} {rv.status}{rv.reviewedAt ? ` ${stamp(rv.reviewedAt)}` : ''}{rv.reviewedBy ? ` by ${rv.reviewedBy}` : ''}
          </div>
        </>)}
      </div>
    </div>
  );
}

export default function GuidedReviewTab({ borrowerId, token, onChanged, onOpenModule, user, fraudFlags, idVerification, refreshKey }) {
  const [view, setView] = useState(null);
  const [err, setErr] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const api = {
    post: (p, b) => axios.post(`${API}/api/admin${p}`, b, { headers }),
    patch: (p, b) => axios.patch(`${API}/api/admin${p}`, b, { headers }),
  };
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/admin/borrowers/${borrowerId}/guided-review`, { headers }); setView(r.data); setErr(null); }
    catch (e) { setErr('The review could not be loaded just now.'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowerId, token]);
  // refreshKey changes when the Underwriting Module closes — whatever was done there shows here at once.
  useEffect(() => { load(); }, [load, refreshKey]);
  const changed = async () => { await load(); if (onChanged) await onChanged(); };
  const open = (stepId) => onOpenModule && onOpenModule(stepId);

  if (err) return <div data-testid="guided-review-error" role="alert" style={{ color: B.red, fontSize: 13 }}>{err}</div>;
  if (!view) return <div style={{ color: B.slate, fontSize: 13 }}>Loading the review…</div>;
  const steps = view.applicants.map(currentStepOf);
  const lead = Math.max(...steps, 1);
  const reached8 = steps.some((n) => n >= 8) || !!(view.step8 && view.step8.decision);
  const leadLabel = (stepByNumber(lead) || {}).label;

  return (
    <div data-testid="guided-review-tab" style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <FraudLine user={user} fraudFlags={fraudFlags} idVerification={idVerification} />
        <button type="button" data-testid="guided-review-open-module" onClick={() => open(MODULE_STEP_FOR[lead] || 'identity')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 18px', borderRadius: 8, border: 'none', background: B.blue, color: B.white, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          Open in Underwriting Module
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17L17 7M9 7h8v8" /></svg>
        </button>
      </div>
      <GuidedStepHeader view={view} />
      <StepStrip view={view} />
      <div data-testid="guided-review-open-items" style={{ display: 'grid', gap: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.ink }}>Open items on {view.applicants.length > 1 && new Set(steps).size > 1 ? 'each applicant\'s current step' : `step ${lead}${leadLabel ? ` · ${leadLabel}` : ''}`}</div>
        <div style={{ display: 'grid', gridTemplateColumns: view.applicants.length > 1 ? 'repeat(auto-fit, minmax(300px, 1fr))' : '1fr', gap: 16 }}>
          {view.applicants.map((a) => <OpenItems key={a.userId} a={a} api={api} onChanged={changed} onOpenModule={open}
            blocking={view.blocking} applicantIds={view.applicants.map((x) => Number(x.userId))} />)}
        </div>
      </div>
      {reached8 && <DecisionCard view={view} onOpenModule={open} />}
    </div>
  );
}
